import {
  OrganizationMember,
  OrganizationRole,
  UserPlanType,
} from 'features/users/users.types';
import {
  UseMutationResult,
  UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { API } from 'api';
import { AxiosResponse } from 'axios';

type UseOrganizationMemberProps = {
  memberId?: number;
  enabled?: boolean;
};

export type UseOrganizationMember = Omit<
  UseQueryResult<OrganizationMember, Error>,
  'data'
> & {
  member?: OrganizationMember;
  saveMember: UseMutationResult<
    AxiosResponse<unknown>,
    Error,
    SaveMemberData,
    unknown
  >;
  updateMember: UseMutationResult<
    AxiosResponse<unknown>,
    Error,
    UpdateMemberData,
    unknown
  >;
  deleteMember: UseMutationResult<AxiosResponse<unknown>, Error, void, unknown>;
};

export type SaveMemberData = {
  firstName: string;
  lastName: string;
  email: string;
  planType: UserPlanType;
  role: OrganizationRole;
};

export type UpdateMemberData = {
  firstName?: string;
  lastName?: string;
  planType?: UserPlanType;
};

const fetchOrganizationMember = async (
  memberId: number,
): Promise<OrganizationMember> => {
  const result = await API.get<OrganizationMember>(
    `organization/members/${memberId}/`,
  );
  return result.data;
};

export const useOrganizationMember = (
  props: UseOrganizationMemberProps = {},
): UseOrganizationMember => {
  const { memberId, enabled = true } = props;
  const queryClient = useQueryClient();

  const { data, ...results } = useQuery<OrganizationMember>({
    queryKey: ['organizationMember', memberId],
    queryFn: () => fetchOrganizationMember(memberId!),
    staleTime: 1000 * 60, // 1 minute
    enabled: enabled && !!memberId,
  });

  const saveMember = useMutation({
    mutationFn: (member: SaveMemberData) =>
      API.post('organization/members/', member),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['organizationMembers'],
      });
    },
  });

  const updateMember = useMutation({
    mutationFn: (update: UpdateMemberData) =>
      API.patch(`organization/members/${memberId}/`, {
        firstName: update.firstName,
        lastName: update.lastName,
        planType: update.planType,
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['organizationMembers'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['organizationMember', memberId],
        }),
      ]);
    },
  });

  const deleteMember = useMutation({
    mutationFn: () => API.delete(`organization/members/${memberId}/`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['organizationMembers'],
      });
    },
  });

  return {
    ...results,
    member: data,
    saveMember,
    updateMember,
    deleteMember,
  };
};
